import axios from 'axios';
import { cachetime } from '../config';
import { getSightDistance } from './sightmap';
import eventHelper from '@/mixins/eventHelper';
import getMacauDateTime from '@/utils/getMacauDateTime';
import { sameCoordGrouper, recalculateCoord } from '@/utils/relocateSameCoordPois';
import { distanceFromSightToPoi } from '@/utils/map';
import poi from '../modules/poi';

const defaultFilter = {types: []}
const PAGE_SIZE = 10;

export const getMonthFilter = (m) => {
  const dates = [getMacauDateTime()];
  for (let i = 1; i < 12; i++) {
    const date = getMacauDateTime(dates[dates.length - 1].getTime());
    const preDate = getMacauDateTime(date.setDate(0)); // last day of previous month
    dates.push(preDate);
  }
  const months = dates.map(date => {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const m = `${year}${month < 10 ? '0' + month : month}`;
    return m;
  });
  const hideOptions = {};
  if (m && m.match(/\d{6}/) && !months.includes(m)) {
    months.push(m);
    hideOptions[m] = true;
  }
  return {
    months: months.sort((a, b) => +b - +a).map(m => ({
      name: m.slice(0, 4) + '/' + m.slice(4),
      value: m,
      hidden: hideOptions[m] || false,
    })),
  };
};

export default function() {
  return {
    namespaced: true,
    state: {
      filter: { types: []},
      search: {
        locale: '',
        month: '',
        type: '',
        keyword: '',
        resultCount: 0,
        whatsonTop: {},
        whatsonResults: [],
        results: []
      },
      // event id - id or whatson/id : Object map to locale
      events: {}
    },
    mutations: {
      SET_FILTER(state, { data, locale }) {
        state.filter = {
          ...state.filter,
          [locale]: {
            data,
            __updatedAt: Date.now()
          },
        }
      },
      RESET_SEARCH(state, { month, type, keyword, locale }) {
        state.search = {
          month, type, keyword, locale,
          resultCount: 0,
          whatsonTop: {},
          whatsonResults: [],
          results: []
        }
      },
      REFRESH_SEARCH_RESULT(state, { resultCount }) {
        state.search = {
          ...state.search,
          resultCount,
          whatsonTop: {},
          whatsonResults: [],
          results: Array.apply(null, Array(resultCount))
        }
      },
      UPDATE_SEARCH_RESULT(state, { page=1, month="", type="", keyword="", locale="", results=[], whatsonTop, isWhatsonEntry=false }) {
        const START_IDX = (page - 1) * PAGE_SIZE;
        state.search.month = month;
        state.search.type = type;
        state.search.keyword = keyword;
        state.search.locale = locale;
        if (whatsonTop) {
          state.search.whatsonTop = whatsonTop;
        }
        if (isWhatsonEntry) {
          state.search.whatsonResults = results;
        }
        results.forEach((el, idx) => {
          state.search.results[START_IDX + idx] = {
            ...el
          };
        });
      },
      UPDATE_EVENT(state, { data, key, locale }) {
        let allEvent = state.events[key] || {};
        state.events = {
          ...state.events,
          [key]: {
            ...allEvent,
            [locale]: {
              ...data,
              __updatedAt: Date.now()
            }
          }
        }
      }
    },
    actions: {
      FETCH_FILTER({ state, commit }, { locale }) {
        if (state.filter[locale]
          && state.filter[locale].data
          && state.filter[locale].__updatedAt
          && Date.now() - state.filter[locale].__updatedAt < cachetime 
        ) return ;

        return axios.get(encodeURI(`/api/enf/whatson_filter`))
          .then((res) => {
            const data = res.data || defaultFilter;

            commit('SET_FILTER', { data: data, locale });
          }).catch(err => console.log(err));      
      },
      SEARCH_EVENT({ state, commit }, { type='', keyword='', page=1, locale='en', m='', filterMonth=false, filterLocation=false, isWhatsonEntry=false }) {

        const sameLocationGrouper = function(pois) {
          const handler = function(set, coordinate, id) {
            if (Array.isArray(coordinate[0])) {
              return coordinate.reduce((set, crd) => {
                return handler(set, crd, id);
              }, set);
            } else {
              const hash = `${coordinate[0]},${coordinate[1]}`
              set[hash] = set[hash] ? [...set[hash], id] : [id];
              return set;
            }
          };
  
          return pois.reduce((set, poi) => {
            return handler(set, poi.coordinate, poi.id);
          }, {});
        };

        const recalculate = function(coordinate, set, id) {
          const round = function(number) {
            return parseFloat(number.toFixed(5));
          };
          const lng = function(coordinate, radian, radius = 20) {
            const distance = round(Math.cos(radian) * radius);
            return parseFloat(coordinate[0]) + (360 * distance / 40075000);
          };
          const lat = function(coordinate, radian, radius = 20) {
            const distance = round(Math.sin(radian) * radius);
            return parseFloat(coordinate[1]) + (360 * distance / 40075000);
          };
          const order = set.indexOf(id);
          const angle = order * 360 / set.length;
          const radian = angle * Math.PI / 180;
          return [lng(coordinate, radian), lat(coordinate, radian)];
        };

        let month = m;

        if (
          state.search.month != month ||
          state.search.type != type ||
          state.search.keyword != keyword ||
          state.search.locale != locale
        ) {
          !isWhatsonEntry && commit('RESET_SEARCH', { month, type, keyword, locale })
        } else if (!state.search.results.slice(PAGE_SIZE * (page - 1), PAGE_SIZE * page).some(e => e == null)) {
          return ;
        }

        const checkMonth = (event) => {
          if (!event.month || !event.month.match(/\d{4}-\d{2}/)) return false;
          let yearRange, monthRange;
          if (m) {
            yearRange = parseInt(m.slice(0, 4));
            monthRange = parseInt(m.slice(4, 6));
          } else {
            const date = getMacauDateTime();
            yearRange = date.getFullYear();
            monthRange = date.getMonth() + 1;
          }
          const [year, month] = event.month.split('-').map(num => parseInt(num));
          return m ? (year == yearRange && month == monthRange) : (year >= yearRange && month >= monthRange);
        };

        const checkLocation = (event) => {
          return event.coordinate && event.coordinate.length > 0;
        };

        return axios.get(encodeURI(`/api/enf/whatson?lang=${locale}&type=${type}&keyword=${keyword}` + (m ? `&m=${m}` : '')))
          .then((res) => {
            let { resultCount=0, top, results=[] } = res.data;
            if (isWhatsonEntry) {
              if (resultCount === 0) {
                throw {
                  response: {
                    status: 404
                  }
                }
              }
            }
            if (!m && !month && results.length > 0) {
              const resultSample = results.find(result => result.month);
              if (resultSample) {
                month = `${resultSample.month}`.replace(/-/g, '').slice(0, 6);
              }
            }
            // adjust for new whatson api
            results = results.map(event => {
              if (!event.coordinate && event.location) {
                event.coordinate = event.location.map(loc => loc.coordinate);
                if (event.coordinate.length === 1) {
                  event.coordinate = event.coordinate[0];
                }
              }
              return event;
            });
            results = eventHelper.methods.sortWhatsonResult(results);
            // filter result with not date and location
            if (filterMonth) results = results.filter(checkMonth);
            if (filterLocation) results = results.filter(checkLocation);
            resultCount = results.length;
            if (resultCount != state.search.resultCount) {
              !isWhatsonEntry && commit('REFRESH_SEARCH_RESULT', { resultCount });
            }

            // adjust coordinate for same location pois
            /*
            const set = sameLocationGrouper(results);
            results = results.map((result) => {
              if (result.coordinate.every(value => Array.isArray(value))) {
                // array of locations
                result.coordinate = result.coordinate.map(point => recalculate(point, set[point], result.id));
              } else {
                // single point
                result.coordinate = recalculate(result.coordinate, set[result.coordinate], result.id);
              }
              return result;
            });
            */
            sameCoordGrouper(results).forEach(sameCoordGroup => {
              sameCoordGroup.forEach((idx, i, arr) => {
                if (results[idx]) {
                  if (Array.isArray(results[idx].coordinate[0])) {
                    results[idx].coordinate = results[idx].coordinate.map(coordinate => recalculateCoord(coordinate, i, arr.length));
                  } else {
                    results[idx].coordinate = recalculateCoord(results[idx].coordinate, i, arr.length);
                  }
                }
              });
            });

            commit('UPDATE_SEARCH_RESULT', { month, type, keyword, locale, whatsonTop: isWhatsonEntry && top, results, isWhatsonEntry });

          }).catch(err => {
            console.log(err);
            throw err;
          });
      },
      FETCH_EVENT({ state, commit, dispatch }, { id='', isWhatson=false, locale='en', ssr }) {
        let key = isWhatson ? `whatson/${id}` : `calendar/${id}`;
        if (state.events[key]
          && state.events[key][locale]
          && state.events[key][locale].__updatedAt
          && Date.now() - state.events[key][locale].__updatedAt < cachetime 
        ) {
          return ;
        }

        return axios.get(encodeURI(`/api/enf/${key}`))
          .then((res) => {
            if (isWhatson) {
              if (eventHelper.methods.parseWhatsonDate(res.data).hidden) {
                // throw {
                //   response: {
                //     status: 404
                //   }
                // }
              }
            }
            const data = res.data || {};
            delete data.pois; // old api still contains pois field, delete to avoid vue reactivity issue
            // data.pois = [];
            // console.log('datapois: ', data.pois)
            const entityCoordinates = data.coordinate || [];
            const coordinate = Array.isArray(entityCoordinates[0]) ? entityCoordinates[0].join('|') : entityCoordinates.join('|');

            commit('UPDATE_EVENT', {
              data,
              key,
              locale
            })

            // ssr no FETCH_POI
            if (ssr) {
              return;
            } else if (coordinate) {
              return dispatch('poi/FETCH_POI', { coordinate, locale })
                .then((pois) => {
                  data.pois = pois.filter(poi => poi.id !== data.id).map((poi) => {
                    return Object.assign(poi, {
                      distance: distanceFromSightToPoi(data, poi),
                    });
                  });

                  commit('UPDATE_EVENT', {
                    data,
                    key,
                    locale
                  })
                })
                .catch(err => {
                  console.log(err);
                  // if (err.response && err.response.status == 404) {
                    throw err;
                  // }
                });
            } else {
              console.warn(key, 'missing coordinate data')
            }
          }).catch(err => {
            console.log(err);
            // if (err.response && err.response.status == 404) {
              throw err;
            // }
          })
      }
    },
    getters: { 
      getSearchedMonth: (state) => () => {
        return state.search.month;
      },
      getTopUI: (state, getters, rootState) => () => {
        return state.search.whatsonTop;
      },
      getSearchResult: (state, getters, rootState) => (options) => {
        const isWhatsonEntry = options && options.isWhatsonEntry;
        const raw = [...state.search[isWhatsonEntry ? 'whatsonResults' : 'results']].filter(res => !!res);
        const { isGeolocationOn, userLocation } = rootState.geolocation;

        if (raw && isGeolocationOn) {
          // sort raw
          raw.sort((a, b) => getSightDistance(a, userLocation) - getSightDistance(b, userLocation));
        }

        return raw;
      },
      getExistSearchResult: (state, getters, rootState) => (
        id, options, packAsGroup=false
      ) => {
        // check options match
        let {
          month='', type='', keyword='', locale='en' 
        } = options;

        if (
          state.search.month != month ||
          state.search.type != type ||
          state.search.keyword != keyword ||
          state.search.locale != locale
        ) {
          return []
        }

        // check target exist
        if (!state.search.results.find(o => o.id == id)) {
          return []
        }

        // return result
        const results = getters.getSearchResult(locale);
        return packAsGroup 
          ? {
            sights: results
          }
          : results; 
      },
      getMonthFilter: (state) => getMonthFilter,
      getFilter: (state) => (locale) => {
        return state.filter[locale] 
          ? (state.filter[locale].data || defaultFilter) 
          : defaultFilter;
      },
      getEvent: (state) => (id, isWhatson, locale) => {
        let key = isWhatson ? `whatson/${id}` : `calendar/${id}`;
        if (state.events[key]
          && state.events[key][locale]
        ) {
          let data = state.events[key][locale];
          data.filters = [];
          isWhatson && data.types && data.types.forEach(type => {
            data.filters.push({
              name: type.name,
              to: `/events/whatson?type=${type.value}`,
            })
          })
          return data;
        } else {
          return {
            gallery: []
          }
        }
      }
    },
    modules: {
      poi: poi(),
    },
  }
}